/**
 * Conversation Crypto Service
 *
 * Provides E2E encryption and decryption for conversation messages
 * and group names. Supports both static device key wrapping and
 * forward secrecy via pre-keys.
 *
 * SECURITY ARCHITECTURE:
 * - Each message uses a fresh random 256-bit session key
 * - Session key is wrapped per-device for all conversation participants
 * - Forward secrecy when pre-keys are available (SPK and/or OTPK)
 * - Messages signed with Ed25519+ML-DSA-65 when the sender has a PQ signing key;
 *   64-byte Ed25519 history still verifies
 * - Conversation metadata (name, member settings, read-state, system events) uses one
 *   random 32-byte key wrapped per attested device (same pin/attest path
 *   as messages). HKDF(conversationId) decrypt remains for legacy blobs.
 *
 * @module services/conversationCryptoService
 */

import {
  encrypt,
  decrypt,
  wrapSessionKey,
  unwrapSessionKey,
  wrapSessionKeyWithPreKeys,
  unwrapSessionKeyWithPreKeys,
  computeRoutingTag,
  verifySignedPreKey,
  signIdentity,
  verifySignature,
  isHybridSignatureEncoding,
  randomBytes,
  toBase64,
  fromBase64,
  concatBytes,
  toBytes,
  deriveKey,
  encryptConversationMetadataField,
  decryptConversationMetadataField,
  type CryptoProfile,
  type IdentityPublicKeys as CryptoIdentityPublicKeys,
  type WrappedKey,
  type PreKeyWrappedKey,
  type SignedPreKeyPublic,
  type OneTimePreKeyPublic,
} from '@adieuu/crypto';
import type {
  SerializedWrappedKey,
  MessageCryptoProfile,
  PublicMessage,
  ClaimedDevicePreKeys,
  PublicDevice,
  MessageSignatureContext,
  SystemEvent,
} from '@adieuu/shared';
import {
  MESSAGE_SIGN_DOMAIN_V1,
  buildMessageSignaturePreimageV2,
} from '@adieuu/shared';
import { deviceStaticAttestationHolds } from './deviceAttestation';

// ============================================================================
// Constants
// ============================================================================

const GROUP_NAME_KDF_INFO = 'adieuu-conv-name-v1';
const MEMBER_SETTINGS_KDF_INFO = 'adieuu-conv-member-settings-v1';

// ============================================================================
// Types
// ============================================================================

export interface RecipientKeys {
  identityId: string;
  signingPublicKey: string;
  /** ML-DSA-65 verifying key when the peer initialized hybrid identity signing. */
  pqSigningPublicKey?: string;
  preferredCryptoProfile: CryptoProfile;
  devices: PublicDevice[];
  preKeys?: ClaimedDevicePreKeys[];
}

export interface EncryptedMessage {
  ciphertext: string;
  nonce: string;
  wrappedKeys: SerializedWrappedKey[];
  signature: string;
  cryptoProfile: MessageCryptoProfile;
  /**
   * Devices that were wrapped with static keys even though forward secrecy
   * was requested (pre-keys unavailable or SPK signature invalid).
   * Always empty when forward secrecy was not requested.
   */
  fsDowngradedDeviceIds: string[];
  /**
   * Devices whose signed pre-key failed signature verification against the
   * recipient's identity key. These fall back to static wrapping rather than
   * being silently skipped. Overlaps with {@link fsDowngradedDeviceIds} only
   * when forward secrecy was requested; otherwise devices appear here but
   * not in `fsDowngradedDeviceIds`.
   */
  spkVerificationFailedDeviceIds: string[];
}

export interface MessageSignatureCheck {
  verified: boolean;
  /** Which signature preimage version verified (undefined when verification failed). */
  signatureVersion?: 1 | 2;
  /**
   * Hybrid signature checked without the sender's ML-DSA-65 key. The failure
   * may clear once that key is fetched; any other failure is final.
   */
  pendingPqKey: boolean;
}

export interface DecryptedMessage extends MessageSignatureCheck {
  plaintext: string;
  sessionKey: Uint8Array;
}

export interface EncryptMessageOptions {
  /**
   * Whether the sender requested forward secrecy for this message. Used only
   * for downgrade reporting: when true, devices that end up statically
   * wrapped are recorded in `fsDowngradedDeviceIds`.
   */
  forwardSecrecyRequested?: boolean;
  /** ML-DSA-65 secret; when present, new sends use the hybrid combiner. */
  pqSigningPrivateKey?: Uint8Array;
}

export interface EncryptedGroupName {
  encryptedName: string;
  nameNonce: string;
}

export interface MemberCustomisation {
  nickname?: string;
  color?: string;
}

export type MemberSettingsMap = Record<string, MemberCustomisation>;

export interface EncryptedMemberSettings {
  encryptedMemberSettings: string;
  memberSettingsNonce: string;
}

export interface EncryptedSystemEvent {
  encryptedSystemEvent: string;
  systemEventNonce: string;
}

// ============================================================================
// Message Encryption
// ============================================================================

/**
 * Encrypt a message for all conversation participants.
 *
 * Flow:
 * 1. Generate a fresh random session key (32 bytes)
 * 2. Encrypt plaintext with session key (ChaCha20-Poly1305 or AES-256-GCM)
 * 3. For each recipient device:
 *    a. If pre-keys available: wrap session key with pre-key exchange (forward secrecy)
 *    b. Otherwise: wrap session key with static device ECDH+KEM keys
 * 4. Sign the v2 preimage (domain, conversation, sender, clientMessageId,
 *    ciphertext, nonce, canonical wrapped keys) with Ed25519+ML-DSA-65 when
 *    a PQ signing secret is provided
 *
 * Devices whose signed pre-key fails signature verification fall back to
 * static wrapping (never silently skipped) and are reported via
 * `spkVerificationFailedDeviceIds` / `fsDowngradedDeviceIds`.
 *
 * @param context - Message context bound into the signature (conversationId,
 *   fromIdentityId, clientMessageId). For edits, pass the original message's
 *   clientMessageId (stable across revisions).
 */
export function encryptMessage(
  plaintext: string,
  recipients: RecipientKeys[],
  signingPrivateKey: Uint8Array,
  context: MessageSignatureContext,
  senderCryptoProfile: CryptoProfile = 'default',
  options?: EncryptMessageOptions
): EncryptedMessage {
  const plaintextBytes = new TextEncoder().encode(plaintext);
  const sessionKey = randomBytes(32);

  const { ciphertext, nonce } = encrypt(sessionKey, plaintextBytes, senderCryptoProfile);

  const wrappedKeys: SerializedWrappedKey[] = [];
  const fsRequested = options?.forwardSecrecyRequested === true;
  const fsDowngradedDeviceIds: string[] = [];
  const spkVerificationFailedDeviceIds: string[] = [];

  for (const recipient of recipients) {
    // Use the sender's profile for wrapping so that message.cryptoProfile
    // can be used consistently during both wrapping and unwrapping.
    // Without per-wrapped-key profile storage, a mismatch here would
    // cause silent decryption failures.
    const profile = senderCryptoProfile;

    for (const device of recipient.devices) {
      if (!deviceStaticAttestationHolds(
        recipient.signingPublicKey,
        device,
        profile,
        recipient.pqSigningPublicKey,
      )) {
        continue;
      }

      const devicePreKeys = recipient.preKeys?.find(
        (pk) => pk.deviceId === device.deviceId
      );

      let spkVerified = false;
      let spk: SignedPreKeyPublic | undefined;

      if (devicePreKeys?.signedPreKey) {
        spk = {
          keyId: devicePreKeys.signedPreKey.keyId,
          ecdhPublicKey: fromBase64(devicePreKeys.signedPreKey.ecdhPublicKey),
          kemPublicKey: fromBase64(devicePreKeys.signedPreKey.kemPublicKey),
          signature: fromBase64(devicePreKeys.signedPreKey.signature),
        };

        const sigPub = fromBase64(recipient.signingPublicKey);
        spkVerified = verifySignedPreKey(
          spk,
          sigPub,
          decodeOptionalPqSigningPublicKey(recipient.pqSigningPublicKey),
        );
        if (!spkVerified) {
          // Never encrypt to an unverified SPK: it may be a substituted key.
          // Fall back to the device's static keys instead of skipping the
          // device, and surface the downgrade to the caller.
          spkVerificationFailedDeviceIds.push(device.deviceId);
        }
      }

      if (spk && spkVerified) {
        let otpk: OneTimePreKeyPublic | undefined;
        if (devicePreKeys!.oneTimePreKey) {
          otpk = {
            keyId: devicePreKeys!.oneTimePreKey.keyId,
            ecdhPublicKey: fromBase64(devicePreKeys!.oneTimePreKey.ecdhPublicKey),
            kemPublicKey: fromBase64(devicePreKeys!.oneTimePreKey.kemPublicKey),
          };
        }

        const wrapped = wrapSessionKeyWithPreKeys(sessionKey, spk, otpk, profile);

        wrappedKeys.push({
          identityId: recipient.identityId,
          ephemeralPublicKey: toBase64(wrapped.ephemeralPublicKey),
          kemCiphertext: toBase64(wrapped.spkKemCiphertext),
          wrappedSessionKey: toBase64(wrapped.wrappedSessionKey),
          wrappingNonce: toBase64(wrapped.wrappingNonce),
          preKeyType: otpk ? 'otpk' : 'spk',
          signedPreKeyId: devicePreKeys!.signedPreKey!.keyId,
          oneTimePreKeyId: otpk ? devicePreKeys!.oneTimePreKey!.keyId : undefined,
          spkKemCiphertext: toBase64(wrapped.spkKemCiphertext),
          otpkKemCiphertext: wrapped.otpkKemCiphertext
            ? toBase64(wrapped.otpkKemCiphertext)
            : undefined,
          routingTag: device.kemPublicKey
            ? computeRoutingTag(device.ecdhPublicKey, device.kemPublicKey)
            : undefined,
          wrapVersion: wrapped.wrapVersion,
        });
      } else {
        if (!device.kemPublicKey) {
          console.warn('[Crypto] Skipping device without kemPublicKey:', device.deviceId);
          continue;
        }

        if (fsRequested) {
          fsDowngradedDeviceIds.push(device.deviceId);
        }

        const recipientPublicKeys: CryptoIdentityPublicKeys = {
          signing: fromBase64(recipient.signingPublicKey),
          ecdh: fromBase64(device.ecdhPublicKey),
          kem: fromBase64(device.kemPublicKey),
          profile,
        };

        const wrapped = wrapSessionKey(sessionKey, recipientPublicKeys, recipient.identityId);

        wrappedKeys.push({
          identityId: recipient.identityId,
          ephemeralPublicKey: toBase64(wrapped.ephemeralPublicKey),
          kemCiphertext: toBase64(wrapped.kemCiphertext),
          wrappedSessionKey: toBase64(wrapped.wrappedSessionKey),
          wrappingNonce: toBase64(wrapped.wrappingNonce),
          preKeyType: 'static',
          routingTag: computeRoutingTag(device.ecdhPublicKey, device.kemPublicKey),
          wrapVersion: wrapped.wrapVersion,
        });
      }
    }
  }

  const ciphertextB64 = toBase64(ciphertext);
  const nonceB64 = toBase64(nonce);

  // v2 signature: binds conversation, sender, and clientMessageId so a
  // malicious server cannot replay this message in another context.
  const preimage = buildMessageSignaturePreimageV2(
    context,
    ciphertextB64,
    nonceB64,
    wrappedKeys
  );
  const signature = signIdentity(
    signingPrivateKey,
    toBytes(preimage),
    options?.pqSigningPrivateKey
  );

  return {
    ciphertext: ciphertextB64,
    nonce: nonceB64,
    wrappedKeys,
    signature: toBase64(signature),
    cryptoProfile: senderCryptoProfile,
    fsDowngradedDeviceIds,
    spkVerificationFailedDeviceIds,
  };
}

// ============================================================================
// Message Decryption
// ============================================================================

/**
 * Decrypt a message received from a conversation participant.
 *
 * @param message - The public message from the server
 * @param myIdentityId - Current user's identity ID
 * @param ecdhPrivateKey - Current device's X25519 private key
 * @param kemPrivateKey - Current device's ML-KEM private key
 * @param senderSigningPublicKey - Sender's Ed25519 public key (base64)
 * @param preKeyPrivateKeys - SPK/OTPK private keys if the message used pre-keys
 * @param senderPqSigningPublicKey - Sender's ML-DSA-65 public key (base64) when hybrid
 */
export function decryptMessage(
  message: PublicMessage,
  myIdentityId: string,
  ecdhPrivateKey: Uint8Array,
  kemPrivateKey: Uint8Array,
  senderSigningPublicKey: string,
  preKeyPrivateKeys?: {
    spkEcdhPrivate?: Uint8Array;
    spkKemPrivate?: Uint8Array;
    otpkEcdhPrivate?: Uint8Array;
    otpkKemPrivate?: Uint8Array;
  },
  resolvedWrappedKey?: SerializedWrappedKey,
  cachedSessionKey?: Uint8Array,
  senderPqSigningPublicKey?: string
): DecryptedMessage {
  if (message.deleted || !message.ciphertext || !message.nonce || !message.wrappedKeys) {
    throw new Error('Cannot decrypt a deleted message');
  }

  const ciphertext = fromBase64(message.ciphertext);
  const nonce = fromBase64(message.nonce);
  const profile = message.cryptoProfile as CryptoProfile;

  let sessionKey: Uint8Array;

  if (cachedSessionKey) {
    sessionKey = cachedSessionKey;
  } else {
    const myWrappedKey = resolvedWrappedKey ?? message.wrappedKeys!.find(
      (wk: SerializedWrappedKey) => wk.identityId === myIdentityId
    );

    if (!myWrappedKey) {
      throw new Error('No wrapped key found for this identity');
    }

    if (myWrappedKey.preKeyType === 'spk' || myWrappedKey.preKeyType === 'otpk') {
      if (
        !preKeyPrivateKeys?.spkEcdhPrivate ||
        !preKeyPrivateKeys?.spkKemPrivate
      ) {
        throw new Error('Pre-key private keys required to decrypt this message');
      }

      const wrapped: PreKeyWrappedKey = {
        ephemeralPublicKey: fromBase64(myWrappedKey.ephemeralPublicKey),
        spkKemCiphertext: fromBase64(myWrappedKey.spkKemCiphertext || myWrappedKey.kemCiphertext),
        otpkKemCiphertext: myWrappedKey.otpkKemCiphertext
          ? fromBase64(myWrappedKey.otpkKemCiphertext)
          : undefined,
        wrappedSessionKey: fromBase64(myWrappedKey.wrappedSessionKey),
        wrappingNonce: fromBase64(myWrappedKey.wrappingNonce),
        signedPreKeyId: myWrappedKey.signedPreKeyId,
        oneTimePreKeyId: myWrappedKey.oneTimePreKeyId,
        wrapVersion: myWrappedKey.wrapVersion,
      };

      sessionKey = unwrapSessionKeyWithPreKeys(
        wrapped,
        preKeyPrivateKeys.spkEcdhPrivate,
        preKeyPrivateKeys.spkKemPrivate,
        preKeyPrivateKeys.otpkEcdhPrivate,
        preKeyPrivateKeys.otpkKemPrivate,
        profile
      );
    } else {
      const wrappedKeyObj: WrappedKey = {
        identityId: myWrappedKey.identityId,
        ephemeralPublicKey: fromBase64(myWrappedKey.ephemeralPublicKey),
        kemCiphertext: fromBase64(myWrappedKey.kemCiphertext),
        wrappedSessionKey: fromBase64(myWrappedKey.wrappedSessionKey),
        wrappingNonce: fromBase64(myWrappedKey.wrappingNonce),
        wrapVersion: myWrappedKey.wrapVersion,
      };

      sessionKey = unwrapSessionKey(
        wrappedKeyObj,
        ecdhPrivateKey,
        kemPrivateKey,
        profile
      );
    }
  }

  const plaintext = decrypt(sessionKey, ciphertext, nonce, profile);
  const plaintextStr = new TextDecoder().decode(plaintext);

  const check = verifyConversationMessageSignature(
    message,
    senderSigningPublicKey,
    senderPqSigningPublicKey
  );

  return { plaintext: plaintextStr, ...check, sessionKey };
}

/**
 * Verify a conversation message signature (v2 context-bound, then legacy v1)
 * without unwrapping or decrypting. Throws only on malformed ciphertext or
 * nonce base64, like {@link decryptMessage}.
 */
export function verifyConversationMessageSignature(
  message: PublicMessage,
  senderSigningPublicKey: string,
  senderPqSigningPublicKey?: string
): MessageSignatureCheck {
  if (!message.ciphertext || !message.nonce || !message.wrappedKeys) {
    return { verified: false, pendingPqKey: false };
  }
  const ciphertext = fromBase64(message.ciphertext);
  const nonce = fromBase64(message.nonce);
  const sigPub = fromBase64(senderSigningPublicKey);
  const pqPub = decodeOptionalPqSigningPublicKey(senderPqSigningPublicKey);
  const signatureBytes = safeFromBase64(message.signature);

  let verified = false;
  let signatureVersion: 1 | 2 | undefined;

  if (signatureBytes) {
    // Try v2 first (context-bound). The domain string inside the preimage
    // prevents a v2 signature from verifying as v1 and vice versa.
    const preimageV2 = buildMessageSignaturePreimageV2(
      {
        conversationId: message.conversationId,
        fromIdentityId: message.fromIdentityId,
        clientMessageId: message.clientMessageId,
      },
      message.ciphertext,
      message.nonce,
      message.wrappedKeys
    );
    try {
      if (verifySignature(sigPub, toBytes(preimageV2), signatureBytes, pqPub)) {
        verified = true;
        signatureVersion = 2;
      }
    } catch {
      // fall through to v1
    }

    if (!verified) {
      // Legacy v1: domain || ciphertext || nonce || JSON(wrappedKeys).
      // Accepted only for messages signed before context binding existed.
      const dataToVerifyV1 = concatBytes(
        toBytes(MESSAGE_SIGN_DOMAIN_V1),
        ciphertext,
        nonce,
        toBytes(JSON.stringify(message.wrappedKeys))
      );
      try {
        if (verifySignature(sigPub, dataToVerifyV1, signatureBytes, pqPub)) {
          verified = true;
          signatureVersion = 1;
        }
      } catch {
        verified = false;
      }
    }
  }

  const pendingPqKey =
    !verified &&
    pqPub === undefined &&
    signatureBytes !== null &&
    isHybridSignatureEncoding(signatureBytes);

  return { verified, signatureVersion, pendingPqKey };
}

/** True when the message carries a hybrid (Ed25519 + ML-DSA-65) signature. */
export function hasHybridMessageSignature(message: Pick<PublicMessage, 'signature'>): boolean {
  const bytes = safeFromBase64(message.signature);
  return bytes !== null && isHybridSignatureEncoding(bytes);
}

function safeFromBase64(value: string | undefined): Uint8Array | null {
  if (!value) return null;
  try {
    return fromBase64(value);
  } catch {
    return null;
  }
}

export function decodeOptionalPqSigningPublicKey(b64?: string): Uint8Array | undefined {
  if (!b64) return undefined;
  try {
    const bytes = fromBase64(b64);
    return bytes.length > 0 ? bytes : undefined;
  } catch {
    return undefined;
  }
}

// ============================================================================
// Conversation metadata key (name, member settings, read-state)
// ============================================================================

const METADATA_KEY_SIZE = 32;

function assertMetadataKey(metadataKey: Uint8Array): void {
  if (metadataKey.length !== METADATA_KEY_SIZE) {
    throw new Error(`Conversation metadata key must be ${METADATA_KEY_SIZE} bytes`);
  }
}

/** Fresh random conversation metadata key. Wrap per attested device; do not HKDF from the id. */
export function generateConversationMetadataKey(): Uint8Array {
  return randomBytes(METADATA_KEY_SIZE);
}

/**
 * Static-wrap the conversation metadata key to attested devices only.
 * Pre-keys are not used: this is a long-lived key (same pin/attest path as messages).
 */
export function wrapConversationMetadataKey(
  metadataKey: Uint8Array,
  recipients: RecipientKeys[],
  profile: CryptoProfile = 'default'
): SerializedWrappedKey[] {
  assertMetadataKey(metadataKey);
  const wrappedKeys: SerializedWrappedKey[] = [];

  for (const recipient of recipients) {
    for (const device of recipient.devices) {
      if (!deviceStaticAttestationHolds(
        recipient.signingPublicKey,
        device,
        profile,
        recipient.pqSigningPublicKey,
      )) {
        continue;
      }
      if (!device.kemPublicKey) {
        continue;
      }

      const recipientPublicKeys: CryptoIdentityPublicKeys = {
        signing: fromBase64(recipient.signingPublicKey),
        ecdh: fromBase64(device.ecdhPublicKey),
        kem: fromBase64(device.kemPublicKey),
        profile,
      };

      const wrapped = wrapSessionKey(metadataKey, recipientPublicKeys, recipient.identityId);

      wrappedKeys.push({
        identityId: recipient.identityId,
        ephemeralPublicKey: toBase64(wrapped.ephemeralPublicKey),
        kemCiphertext: toBase64(wrapped.kemCiphertext),
        wrappedSessionKey: toBase64(wrapped.wrappedSessionKey),
        wrappingNonce: toBase64(wrapped.wrappingNonce),
        preKeyType: 'static',
        routingTag: computeRoutingTag(device.ecdhPublicKey, device.kemPublicKey),
        wrapVersion: wrapped.wrapVersion,
      });
    }
  }

  return wrappedKeys;
}

function serializedWrapToWrappedKey(entry: SerializedWrappedKey): WrappedKey {
  return {
    identityId: entry.identityId,
    ephemeralPublicKey: fromBase64(entry.ephemeralPublicKey),
    kemCiphertext: fromBase64(entry.kemCiphertext),
    wrappedSessionKey: fromBase64(entry.wrappedSessionKey),
    wrappingNonce: fromBase64(entry.wrappingNonce),
    wrapVersion: entry.wrapVersion,
  };
}

/**
 * Unwrap the conversation metadata key for this device (static wraps only).
 */
export function unwrapConversationMetadataKey(
  wrappedKeys: SerializedWrappedKey[],
  myIdentityId: string,
  ecdhPrivateKey: Uint8Array,
  kemPrivateKey: Uint8Array,
  myRoutingTag?: string,
  profile: CryptoProfile = 'default'
): Uint8Array | null {
  const mine = wrappedKeys.filter(
    (wk) => wk.identityId === myIdentityId && wk.preKeyType === 'static'
  );
  if (mine.length === 0) return null;

  const ordered: SerializedWrappedKey[] = [];
  if (myRoutingTag) {
    const tagged = mine.find((wk) => wk.routingTag === myRoutingTag);
    if (tagged) ordered.push(tagged);
  }
  for (const wk of mine) {
    if (!ordered.includes(wk)) ordered.push(wk);
  }

  for (const entry of ordered) {
    try {
      return unwrapSessionKey(
        serializedWrapToWrappedKey(entry),
        ecdhPrivateKey,
        kemPrivateKey,
        profile
      );
    } catch {
      // Wrong device wrap for this identity.
    }
  }
  return null;
}

// ============================================================================
// Group Name Encryption
// ============================================================================

/**
 * Legacy HKDF(conversationId) name key. Only for decrypting blobs written
 * before wrapped metadata keys existed.
 */
function deriveGroupNameKey(
  conversationId: string,
  profile: CryptoProfile = 'default'
): Uint8Array {
  const ikm = toBytes(conversationId);
  return deriveKey({ ikm, info: GROUP_NAME_KDF_INFO, length: 32 }, profile);
}

/**
 * Encrypt a group name with the conversation metadata key (AAD-bound).
 */
export function encryptGroupName(
  name: string,
  conversationId: string,
  metadataKey: Uint8Array,
  profile: CryptoProfile = 'default'
): EncryptedGroupName {
  assertMetadataKey(metadataKey);
  const plaintext = new TextEncoder().encode(name);
  const { ciphertext, nonce } = encryptConversationMetadataField(
    metadataKey,
    plaintext,
    conversationId,
    'name',
    profile
  );

  return {
    encryptedName: toBase64(ciphertext),
    nameNonce: toBase64(nonce),
  };
}

/** @internal Test helper: produce a pre-migration HKDF name blob. */
export function encryptGroupNameLegacyHkdf(
  name: string,
  conversationId: string,
  profile: CryptoProfile = 'default'
): EncryptedGroupName {
  const key = deriveGroupNameKey(conversationId, profile);
  const plaintext = new TextEncoder().encode(name);
  const { ciphertext, nonce } = encrypt(key, plaintext, profile);
  return {
    encryptedName: toBase64(ciphertext),
    nameNonce: toBase64(nonce),
  };
}

/**
 * Decrypt a group name with the wrapped conversation metadata key.
 */
export function decryptGroupNameWithKey(
  encryptedName: string,
  nameNonce: string,
  conversationId: string,
  metadataKey: Uint8Array,
  profile: CryptoProfile = 'default'
): string {
  assertMetadataKey(metadataKey);
  const plaintext = decryptConversationMetadataField(
    metadataKey,
    fromBase64(encryptedName),
    fromBase64(nameNonce),
    conversationId,
    'name',
    profile
  );
  return new TextDecoder().decode(plaintext);
}

/**
 * Decrypt a legacy HKDF(conversationId) group name.
 */
export function decryptGroupName(
  encryptedName: string,
  nameNonce: string,
  conversationId: string,
  profile: CryptoProfile = 'default'
): string {
  const key = deriveGroupNameKey(conversationId, profile);
  const ciphertext = fromBase64(encryptedName);
  const nonce = fromBase64(nameNonce);
  const plaintext = decrypt(key, ciphertext, nonce, profile);
  return new TextDecoder().decode(plaintext);
}

// ============================================================================
// Member Settings Encryption
// ============================================================================

function deriveMemberSettingsKey(
  conversationId: string,
  profile: CryptoProfile = 'default'
): Uint8Array {
  const ikm = toBytes(conversationId);
  return deriveKey({ ikm, info: MEMBER_SETTINGS_KDF_INFO, length: 32 }, profile);
}

/**
 * Encrypt per-member customisations with the conversation metadata key (AAD-bound).
 */
export function encryptMemberSettings(
  settings: MemberSettingsMap,
  conversationId: string,
  metadataKey: Uint8Array,
  profile: CryptoProfile = 'default'
): EncryptedMemberSettings {
  assertMetadataKey(metadataKey);
  const plaintext = new TextEncoder().encode(JSON.stringify(settings));
  const { ciphertext, nonce } = encryptConversationMetadataField(
    metadataKey,
    plaintext,
    conversationId,
    'member-settings',
    profile
  );

  return {
    encryptedMemberSettings: toBase64(ciphertext),
    memberSettingsNonce: toBase64(nonce),
  };
}

/** @internal Test helper: produce a pre-migration HKDF member-settings blob. */
export function encryptMemberSettingsLegacyHkdf(
  settings: MemberSettingsMap,
  conversationId: string,
  profile: CryptoProfile = 'default'
): EncryptedMemberSettings {
  const key = deriveMemberSettingsKey(conversationId, profile);
  const plaintext = new TextEncoder().encode(JSON.stringify(settings));
  const { ciphertext, nonce } = encrypt(key, plaintext, profile);
  return {
    encryptedMemberSettings: toBase64(ciphertext),
    memberSettingsNonce: toBase64(nonce),
  };
}

export function decryptMemberSettingsWithKey(
  encryptedSettings: string,
  nonce: string,
  conversationId: string,
  metadataKey: Uint8Array,
  profile: CryptoProfile = 'default'
): MemberSettingsMap {
  assertMetadataKey(metadataKey);
  const plaintext = decryptConversationMetadataField(
    metadataKey,
    fromBase64(encryptedSettings),
    fromBase64(nonce),
    conversationId,
    'member-settings',
    profile
  );
  return JSON.parse(new TextDecoder().decode(plaintext)) as MemberSettingsMap;
}

/**
 * Decrypt legacy HKDF(conversationId) member settings.
 */
export function decryptMemberSettings(
  encryptedSettings: string,
  nonce: string,
  conversationId: string,
  profile: CryptoProfile = 'default'
): MemberSettingsMap {
  const key = deriveMemberSettingsKey(conversationId, profile);
  const ciphertext = fromBase64(encryptedSettings);
  const nonceBytes = fromBase64(nonce);
  const plaintext = decrypt(key, ciphertext, nonceBytes, profile);
  return JSON.parse(new TextDecoder().decode(plaintext)) as MemberSettingsMap;
}

// ============================================================================
// System event encryption (conversation metadata key)
// ============================================================================

function parseSystemEventPayload(plaintext: string): SystemEvent {
  const parsed: unknown = JSON.parse(plaintext);
  if (!parsed || typeof parsed !== 'object') {
    throw new Error('Invalid system event payload');
  }
  const o = parsed as Record<string, unknown>;
  if (typeof o.type !== 'string' || !o.type || typeof o.identityId !== 'string' || !o.identityId) {
    throw new Error('Invalid system event payload');
  }
  const event: SystemEvent = { type: o.type, identityId: o.identityId };
  if (typeof o.displayName === 'string') event.displayName = o.displayName;
  if (typeof o.username === 'string') event.username = o.username;
  if (typeof o.actorIdentityId === 'string') event.actorIdentityId = o.actorIdentityId;
  if (typeof o.actorDisplayName === 'string') event.actorDisplayName = o.actorDisplayName;
  if (typeof o.actorUsername === 'string') event.actorUsername = o.actorUsername;
  return event;
}

/**
 * Encrypt a system event (including display names) with the conversation metadata key.
 */
export function encryptSystemEvent(
  event: SystemEvent,
  conversationId: string,
  metadataKey: Uint8Array,
  profile: CryptoProfile = 'default'
): EncryptedSystemEvent {
  assertMetadataKey(metadataKey);
  const plaintext = new TextEncoder().encode(JSON.stringify(event));
  const { ciphertext, nonce } = encryptConversationMetadataField(
    metadataKey,
    plaintext,
    conversationId,
    'system-event',
    profile
  );
  return {
    encryptedSystemEvent: toBase64(ciphertext),
    systemEventNonce: toBase64(nonce),
  };
}

export function decryptSystemEvent(
  encryptedSystemEvent: string,
  systemEventNonce: string,
  conversationId: string,
  metadataKey: Uint8Array,
  profile: CryptoProfile = 'default'
): SystemEvent {
  assertMetadataKey(metadataKey);
  const plaintext = decryptConversationMetadataField(
    metadataKey,
    fromBase64(encryptedSystemEvent),
    fromBase64(systemEventNonce),
    conversationId,
    'system-event',
    profile
  );
  return parseSystemEventPayload(new TextDecoder().decode(plaintext));
}
